/**
 * The series colours, and the rules that keep them honest.
 *
 * These are **validated, not chosen**. The eight hues below are the reference
 * categorical palette's dark steps, run through the `dataviz` skill's
 * validator against *this app's* chart surface rather than the reference one.
 * The meteogram draws on `--color-bg`, so since Phase 10 that is both themes'
 * ground, and the set was re-validated on each (2026-09-27):
 *
 * ```
 * node scripts/validate_palette.js \
 *   "#3987e5,#d95926,#199e70,#c98500,#d55181,#008300,#9085e9,#e66767" \
 *   --mode dark --surface "#201e1d"
 * → lightness band PASS · chroma floor PASS
 *   CVD separation PASS (worst adjacent ΔE 8.4, protan · tritan 8.7)
 *   normal-vision floor PASS (worst adjacent ΔE 19.3)
 *   contrast vs surface PASS (all ≥ 3:1)
 *
 * node scripts/validate_palette.js "<the same eight>" --mode light --surface "#f3f2f2"
 * → lightness band PASS · chroma floor PASS
 *   CVD separation PASS (worst adjacent ΔE 8.4, protan · tritan 8.7)
 *   normal-vision floor PASS (worst adjacent ΔE 19.3)
 *   contrast vs surface WARN: #c98500 2.75, #9085e9 2.80, #e66767 2.89
 * ```
 *
 * **One order serves both themes**, so there is no light order and dark order
 * to keep in step (Phase 10 plan §7). Every hard gate passes on both grounds.
 * The light theme's contrast WARN on slots 4, 7 and 8 is the validator's
 * *relief* case — visible labels or a table view — and the table view already
 * exists: `ComparePanel` is this chart's accessible form, printing every value
 * as a number, and the legend names every line. The reference palette's own
 * light steps were measured too and do worse on this ground (four slots below
 * 3:1, the worst 1.94), because they were tuned for `#fcfcfb`.
 *
 * The *adjacent* pairlist is the right one here: this is a line chart, and the
 * pairs a reader has to tell apart are the ones next to each other in the
 * legend. (Under `--pairs all` no eight-hue ordering clears the floors, which
 * is why scatter and choropleth carry a series cap and a line chart does not.)
 *
 * Three rules come with them and none is cosmetic:
 *
 * * **Fixed order, never cycled.** Slot 1 is the first model in registry order
 *   and stays there. A ninth series would fold rather than generate a hue —
 *   there are six models, so this is a rule rather than a limit.
 * * **Colour follows the entity, not its rank.** Turning GFS off in the legend
 *   must not repaint HRRR. `colorFor` keys on the *model id* out of a fixed
 *   table, so a filter cannot move a hue.
 * * **The reference series are not categorical slots.** The official NWS
 *   forecast is the app's `--text` and the observed trace is `--success`: they
 *   are roles rather than identities, and each carries a second encoding as
 *   well as its colour — the official line is heavier, and observations are
 *   *dots* and only ever in the past. A forecast and a measurement are
 *   different kinds of thing and a chart that draws them identically invites
 *   reading the past as a forecast.
 */

/** The validated categorical order, both themes. Slots are assigned, never cycled. */
export const SERIES_HUES = [
  '#3987e5', // blue
  '#d95926', // orange
  '#199e70', // aqua
  '#c98500', // yellow
  '#d55181', // magenta
  '#008300', // green
  '#9085e9', // violet
  '#e66767', // red
] as const

/**
 * Model id -> slot, in the registry's own order.
 *
 * A table rather than "index in whatever list arrived", because the list that
 * arrives is the deployment's enabled set: a checkout that does not ingest RAP
 * must not hand RAP's hue to NAM, or two deployments' screenshots disagree
 * about which line is which.
 */
export const MODEL_ORDER = ['hrrr', 'hrrr-subh', 'rap', 'nam', 'nbm', 'gfs'] as const

const SLOTS = new Map<string, string>(
  MODEL_ORDER.map((id, index) => [id, SERIES_HUES[index % SERIES_HUES.length]]),
)

/** The role colours, as CSS custom properties so the panel owns them. */
export const OFFICIAL_COLOR = 'var(--color-text)'
export const OBSERVED_COLOR = 'var(--color-success)'
/** Everything that is not one of ours, and not one of the two roles. */
export const FALLBACK_COLOR = 'var(--color-muted)'

export function colorFor(kind: string, id: string): string {
  if (kind === 'official') return OFFICIAL_COLOR
  if (kind === 'observed') return OBSERVED_COLOR
  return SLOTS.get(id) ?? FALLBACK_COLOR
}

/** Line weight. The reference is heavier than the models it is a reference for. */
export function widthFor(kind: string): number {
  return kind === 'official' ? 3 : 2
}
